import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Toast } from 'react-vant'
import Header from '@/components/Header'
import Back from '@/components/Back'
import FixedBottom from '@/components/FixedBottom'
import CustomButton from '@/components/CustomButton'
import UploadFile from '@/components/UploadFile'
import { type KycFormData, DocumentType, KycMode } from '../types'

interface StepUploadDocumentProps {
  formData: KycFormData
  updateFormData: (data: Partial<KycFormData>) => void
  onNext: () => void
  onBack: () => void
  mode: KycMode
  canProceed?: boolean
}

export default function StepUploadDocument({ formData, updateFormData, onNext, onBack, mode, canProceed = true }: StepUploadDocumentProps) {
  const { t } = useTranslation()
  const isIdCard = formData.identity_type === DocumentType.IdCard
  const isReadonly = mode === KycMode.View

  const [frontFile, setFrontFile] = useState<File | null>(null)
  const [backFile, setBackFile] = useState<File | null>(null)
  const [frontUploading, setFrontUploading] = useState(false)
  const [backUploading, setBackUploading] = useState(false)

  // 处理正面图片变化（包括删除）
  const handleFrontFileChange = useCallback((file: File | null) => {
    setFrontFile(file)
    // 如果删除了图片，同时清除formData中的URL
    if (!file) {
      if (isIdCard) {
        updateFormData({ idCardFrontUrl: '' })
      } else {
        updateFormData({ passportFrontUrl: '' })
      }
    }
  }, [isIdCard, updateFormData])

  // 处理背面图片变化（包括删除）
  const handleBackFileChange = useCallback((file: File | null) => {
    setBackFile(file)
    // 如果删除了图片，同时清除formData中的URL
    if (!file) {
      updateFormData({ idCardBackUrl: '' })
    }
  }, [updateFormData])

  const handleNext = useCallback(() => {
    if (canProceed && !isUploading) {
      onNext()
    }
  }, [onNext, canProceed])

  // 处理上传成功
  const handleFrontUploadSuccess = useCallback((url: string) => {
    if (isIdCard) {
      updateFormData({ idCardFrontUrl: url })
    } else {
      updateFormData({ passportFrontUrl: url })
    }
    setFrontUploading(false)
  }, [isIdCard, updateFormData])

  const handleBackUploadSuccess = useCallback((url: string) => {
    updateFormData({ idCardBackUrl: url })
    setBackUploading(false)
  }, [updateFormData])

  const handleFrontUploadStart = useCallback(() => {
    setFrontUploading(true)
  }, [])

  const handleBackUploadStart = useCallback(() => {
    setBackUploading(true)
  }, [])

  const handleFrontUploadError = useCallback((error: string) => {
    setFrontUploading(false)
    Toast.fail(error || t('上传失败'))
  }, [t])

  const handleBackUploadError = useCallback((error: string) => {
    setBackUploading(false)
    Toast.fail(error || t('上传失败'))
  }, [t])

  const getTitle = () => {
    return isIdCard ? t('上传身份证') : t('上传护照')
  }

  // 是否正在上传
  const isUploading = frontUploading || backUploading

  // 按钮是否禁用
  const isButtonDisabled = !canProceed || isUploading

  return (
    <div className="flex flex-col h-full">
      <Header
        leftChildren={<Back onClick={onBack} />}
      />

      <div className="flex-1 px-5 pt-4 pb-24 overflow-y-auto">
        {/* 标题 */}
        <div className="text-7 font-medium text-[#1D2129] mb-3">
          {getTitle()}
        </div>

        {/* 身份证上传区域 */}
        {isIdCard ? (
          <>
            {/* 身份证正面 */}
            <div className="mb-6">
              <div className="text-base text-[#1D2129] font-medium mb-3">
                {t('身份证正面')}
              </div>
              <UploadFile
                value={frontFile}
                onChange={handleFrontFileChange}
                onUploadSuccess={handleFrontUploadSuccess}
                onUploadStart={handleFrontUploadStart}
                onUploadError={handleFrontUploadError}
                placeholder="/images/my/idcard-placeholder-front.png"
                uploadedUrl={formData.idCardFrontUrl}
                autoUpload
                maxSize={2}
                className="aspect-[16/9.42]"
                uploadIcon="/images/my/icons/camera.svg"
              />
            </div>

            {/* 身份证背面 */}
            <div className="mb-6">
              <div className="text-base text-[#1D2129] font-medium mb-3">
                {t('身份证背面')}
              </div>
              <UploadFile
                value={backFile}
                onChange={handleBackFileChange}
                onUploadSuccess={handleBackUploadSuccess}
                onUploadStart={handleBackUploadStart}
                onUploadError={handleBackUploadError}
                placeholder="/images/my/idcard-placeholder-back.png"
                uploadedUrl={formData.idCardBackUrl}
                autoUpload
                maxSize={2}
                className="aspect-[16/9.42]"
                uploadIcon="/images/my/icons/camera.svg"
              />
            </div>
          </>
        ) : (
          <>
            {/* 护照个人信息页 */}
            <div className="mb-6">
              <div className="block text-base text-[#1D2129] font-medium mb-3">
                {t('个人信息页')}
              </div>
              <UploadFile
                value={frontFile}
                onChange={handleFrontFileChange}
                onUploadSuccess={handleFrontUploadSuccess}
                onUploadStart={handleFrontUploadStart}
                onUploadError={handleFrontUploadError}
                placeholder="/images/my/passport-placeholder.png"
                uploadedUrl={formData.passportFrontUrl}
                autoUpload
                maxSize={2}
                className="aspect-[16/19]"
                uploadIcon="/images/my/icons/camera.svg"
              />
            </div>
          </>
        )}
      </div>

      {/* 底部按钮 */}
      {!isReadonly && (
        <FixedBottom className="bg-white">
          <CustomButton onClick={handleNext} disabled={isButtonDisabled} loading={isUploading}>
            {t('下一步')}
          </CustomButton>
        </FixedBottom>
      )}
    </div>
  )
}
